import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import type { Stripe, StripeConstructor, StripeElements } from '@stripe/stripe-js'
import {
  StripeBillingDetails,
  StripePaymentIntent
} from 'components/ordering/checkout/stripe/cloudsuite-stripe-backend.js'
import {
  parseStartPaymentError,
  StripeStartPaymentError,
} from 'components/ordering/checkout/stripe/third-party/start-payment-error-parser.js'
import { appearance, paymentElementLayout } from 'components/ordering/checkout/stripe/third-party/appearance.js'


const isSomething = <T>(maybe: T | undefined | null): maybe is T => maybe !== null && maybe !== undefined
export const THIRD_PARTY_STRIPE_API = Symbol('THIRD_PARTY_STRIPE_API')


export type ThirdPartyStripeApi = {
  initialize(paymentIntent: StripePaymentIntent): Promise<void>
  startPayment(returnUrl: string): Promise<void | StripeStartPaymentError>
  initializePaymentElement(paymentIntent: StripePaymentIntent, paymentElementDiv: HTMLDivElement, readyCallback?: () => void): void
  initializeExpressCheckoutElement(paymentIntent: StripePaymentIntent, expressCheckoutDiv: HTMLDivElement, readyCallback?: () => void): void
}


type FakeStripeElement = {
  div: HTMLDivElement,
  paymentIntent: StripePaymentIntent
}

@registerDependency(FAKE, THIRD_PARTY_STRIPE_API)
export class FakeThirdPartyStripeApi implements ThirdPartyStripeApi {
  startPaymentError?: StripeStartPaymentError
  paymentIntent?: StripePaymentIntent
  returnUrl?: string
  paymentElement?: FakeStripeElement
  expressCheckoutElements: FakeStripeElement[] = []

  initialize = async (paymentIntent: StripePaymentIntent): Promise<void> => {
    this.paymentIntent = paymentIntent
  }

  startPayment = async (returnUrl: string): Promise<void | StripeStartPaymentError> => {
    if (isSomething(this.startPaymentError)) {
      return this.startPaymentError
    }

    this.returnUrl = returnUrl
  }

  initializePaymentElement = (paymentIntent: StripePaymentIntent, paymentElementDiv: HTMLDivElement, readyCallback?: () => void): void => {
    if (!this.paymentIntent) throw new Error('Stripe not initialized')
    this.paymentElement = {
      div: paymentElementDiv,
      paymentIntent: paymentIntent
    }
    readyCallback?.()
  }

  initializeExpressCheckoutElement = (paymentIntent: StripePaymentIntent, expressCheckoutElementDiv: HTMLDivElement, readyCallback?: () => void): void => {
    if (!this.paymentIntent) throw new Error('Stripe not initialized')
    this.expressCheckoutElements.push({
      div: expressCheckoutElementDiv,
      paymentIntent: paymentIntent
    })
    readyCallback?.()
  }
}


@registerDependency(REAL, THIRD_PARTY_STRIPE_API)
export class RealThirdPartyStripeApi implements ThirdPartyStripeApi {
  private _stripe?: Stripe
  private _full_user_name?: string
  private _billing_details?: StripeBillingDetails

  private get stripe(): Stripe {
    if (isSomething(this._stripe)) { return this._stripe }
    throw new Error('Stripe unknown, is Stripe properly initialized?')
  }

  private set stripe(value: Stripe | null) {
    if (isSomething(value)) { this._stripe = value }
    else { throw new Error('Loading Stripe returned "null".') }
  }

  private _elements?: StripeElements

  private get elements(): StripeElements {
    if (isSomething(this._elements)) { return this._elements }
    throw new Error('StripeElements unknown, is Stripe properly initialized?')
  }

  private set elements(value: StripeElements) {
    this._elements = value
  }

  private get full_user_name(): string {
    if (isSomething(this._full_user_name)) { return this._full_user_name }
    throw new Error('Full user name unknown, is Stripe properly initialized?')
  }

  loadStripeScript = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const element: HTMLScriptElement = document.createElement('script')
      element.defer = true
      // https://github.com/stripe/stripe-js/blob/master/README.md#versioning
      element.src = 'https://js.stripe.com/basil/stripe.js'
      element.onload = () => resolve()
      element.onerror = () => reject(new Error('Failed to load Stripe script'))
      document.body.appendChild(element)
    })
  }

  initialize = async (paymentIntent: StripePaymentIntent): Promise<void> => {
    try {
      await this.loadStripeScript()
      const stripeInstance = window.Stripe?.(paymentIntent.publicApiKey)
      if (!stripeInstance) {
        throw new Error('Stripe instance can\'t be found on the window')
      }
      this.stripe = stripeInstance
      this.elements = this.stripe.elements({
        clientSecret: paymentIntent.clientSecret,
        locale: paymentIntent.locale,
        appearance,
      })
    } catch (error) {
      console.error('Error loading Stripe', error)
    }

    this._full_user_name = paymentIntent.fullUserName
    this._billing_details = paymentIntent.billingDetails
  }

  initializePaymentElement(paymentIntent: StripePaymentIntent, paymentElementDiv: HTMLDivElement, readyCallback?: () => void): void {
    const paymentElement = this.elements.create(
      'payment',
      {
        paymentMethodOrder: paymentIntent.paymentMethods,
        layout: paymentElementLayout,
        fields: {
          billingDetails: {
            name: 'never',
            ...(paymentIntent.billingDetails.enforceNeverCollect ? { address: 'never' as const } : {}),
          }
        }
      },
    )
    paymentElement.mount(paymentElementDiv)

    if (readyCallback !== undefined)
      paymentElement.on('ready', readyCallback)
  }

  initializeExpressCheckoutElement(paymentIntent: StripePaymentIntent, expressCheckoutElementDiv: HTMLDivElement, readyCallback?: () => void): void {
    const expressCheckoutElement = this.elements.create('expressCheckout')
    expressCheckoutElement.mount(expressCheckoutElementDiv)

    expressCheckoutElement.on('click', (event: any) => {
      const options = {
        emailRequired: true
      }
      event.resolve(options)
    })

    expressCheckoutElement.on('confirm', async () => {
      await this.startPayment(paymentIntent.returnUrl)
    })

    if (readyCallback !== undefined)
      expressCheckoutElement.on('ready', readyCallback)
  }

  startPayment = async (returnUrl: string): Promise<void | StripeStartPaymentError> => {
    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            name: this.full_user_name,
            phone: this._billing_details?.phoneNumber ?? null,
            address: {
              postal_code: this._billing_details?.address?.zipcode ?? null,
              city: this._billing_details?.address?.city ?? null,
              country: this._billing_details?.address?.countryCode ?? null,
              line1: this._billing_details?.address?.street ?? null,
              state: this._billing_details?.address?.state ?? null,
              line2: this._billing_details?.address?.line2 ?? null,
            }
          }
        }
      },
    })

    /**
     * This point will only be reached if there is an immediate error returned by stripe.js.
     * If confirmPayment succeeds, it will redirect before ever getting here.
     */
    console.warn(error)
    return parseStartPaymentError(error)
  }
}

declare global {
  interface Window {
    Stripe?: StripeConstructor | undefined
  }
}
